import { lt } from '@mews/framework';
import { Container, formatFileSize, Stack } from '@optimus-web/core';
import { ReservationContractAttachment } from '@mews-commander/data-access';
import { LocalizedDateTimeFromUtc, TagList } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { ViewFileCard } from '../../../common/components/ViewFileCard';
import { getFileExtensionMimeType } from '../utils';
import { DetailItem } from '../../../common/components/DetailItem';
import { useGetServiceNamesByIdQuery } from '../../../common/queries/useGetServiceNamesByIdQuery';
import { useOpenAttachmentInNewTab } from '../hooks/useOpenAttachmentInNewTab';

export const AttachmentDetailsTab = ({ attachment }: { attachment: ReservationContractAttachment }) => {
    const { mutate: openAttachment, isPending } = useOpenAttachmentInNewTab();

    const { data: serviceNamesById } = useGetServiceNamesByIdQuery();
    const services = attachment.services.map(({ id }) => serviceNamesById[id]);

    const detailsItems = [
        { id: 'name', label: lt('ContractsAttachmentLibraryCustomFileName'), value: attachment.customName },
        {
            id: 'fileName',
            label: lt('ContractsAttachmentLibraryFileName'),
            value: attachment.fileName,
        },
        {
            id: 'services',
            label: lt('ContractsAttachmentLibraryApplicableServices'),
            value: attachment ? <TagList tagsVariant="basicSubtle" tags={services} /> : null,
        },
        {
            id: 'fileType',
            label: lt('ContractsFileTypeLabel'),
            value: getFileExtensionMimeType(attachment.mimeType),
        },
        {
            id: 'size',
            label: lt('ContractsSizeLabel'),
            value: attachment.size ? formatFileSize(attachment.size) : lt('Unknown'),
        },
        {
            id: 'created',
            label: lt('Created'),
            value: (
                <Stack vertical itemAlignment="end">
                    <LocalizedDateTimeFromUtc utcString={attachment.createdUtc} /> {attachment.creatorProfileName}
                </Stack>
            ),
        },
        {
            id: 'updated',
            label: lt('Updated'),
            value: (
                <Stack vertical itemAlignment="end">
                    <LocalizedDateTimeFromUtc utcString={attachment.updatedUtc} /> {attachment.updaterProfileName}
                </Stack>
            ),
        },
        { id: 'notes', label: lt('Note'), value: attachment.notes ?? UnicodeEntity.Dash },
    ];

    return (
        <Container padding={['300', '0', '300', '0']}>
            <Stack vertical spacing="300">
                <ViewFileCard
                    fileName={attachment.fileName}
                    fileId={attachment.id}
                    size={attachment.size}
                    openFile={openAttachment}
                    isPending={isPending}
                />
                <Stack spacing="150" as="ul" vertical>
                    {detailsItems.map(({ id, label, value }, index) => (
                        <DetailItem key={id} label={label} value={value} last={index === detailsItems.length - 1} />
                    ))}
                </Stack>
            </Stack>
        </Container>
    );
};
